import type { SpaceApiResponse } from "./types";
import { notifyStatusChange } from "./status-notify";
import { setVisibleInterval } from "./visible-interval";

/**
 * Same-origin path, proxied server-side (see netlify.toml / nginx.conf) to
 * https://spaceapi.sfz-aalen.space/api/spaceapi.json. That host doesn't send
 * Access-Control-Allow-Origin, so a direct browser fetch is blocked by CORS
 * even though the request itself succeeds - routing it through our own
 * origin sidesteps that entirely.
 */
const SPACEAPI_URL = "/api/spacestatus.json";
/**
 * Short enough that a status flip shows up (and chimes) practically as it happens.
 * The cost stays low because polling is skipped entirely while the tab is hidden,
 * and the endpoint is a small static JSON document.
 */
const REFRESH_INTERVAL_MS = 10_000;

/** Writes only on an actual change - `.status-bar` is aria-live="polite", so an
    unconditional textContent write on every 10s poll would re-announce the exact
    same status to screen reader users purely because a text node got replaced,
    even though nothing changed. */
function setText(el: HTMLElement | null, value: string): void {
  if (el && el.textContent !== value) el.textContent = value;
}

function render(container: HTMLElement, data: SpaceApiResponse): void {
  const { open, lastchange, message } = data.state;
  const headline = container.querySelector<HTMLElement>(".status-headline");
  const meta = container.querySelector<HTMLElement>(".status-meta");

  container.dataset.state = open ? "open" : "closed";

  setText(headline, `Der Hackspace ist derzeit ${open ? "geöffnet" : "geschlossen"}`);

  const parts: string[] = [];
  if (message) parts.push(message);
  if (lastchange) {
    const date = new Date(lastchange * 1000);
    parts.push(`Letzte Statusänderung: ${date.toLocaleString("de-DE")}`);
  }
  setText(meta, parts.join(" · "));
}

function renderError(container: HTMLElement): void {
  container.dataset.state = "error";
  const headline = container.querySelector<HTMLElement>(".status-headline");
  const meta = container.querySelector<HTMLElement>(".status-meta");
  setText(headline, "Space-Status derzeit nicht abrufbar");
  setText(meta, "");
}

function renderHeaderLine(line: HTMLElement, data: SpaceApiResponse): void {
  const { open } = data.state;
  line.dataset.state = open ? "open" : "closed";
  line.setAttribute("aria-label", open ? "Space ist derzeit geöffnet" : "Space ist derzeit geschlossen");
}

function renderHeaderLineError(line: HTMLElement): void {
  line.dataset.state = "error";
  line.setAttribute("aria-label", "Space-Status derzeit nicht abrufbar");
}

export function initSpaceStatus(): void {
  const container = document.getElementById("spaceStatus");
  const headerLine = document.getElementById("headerStatusLine");
  if (!container && !headerLine) return;

  // null until the first successful reading, so page load itself never notifies.
  let lastKnownOpen: boolean | null = null;

  // The interval tick and the "tab became visible again" handler can both want to
  // refresh at nearly the same moment; without this, two overlapping fetches that
  // both observe the same status flip would each compare against the same stale
  // lastKnownOpen and fire the toast/chime twice for one transition.
  let inFlight: Promise<void> | null = null;

  const refresh = (): Promise<void> => {
    if (inFlight) return inFlight;

    inFlight = (async () => {
      try {
        const res = await fetch(SPACEAPI_URL, { cache: "no-store" });
        if (!res.ok) throw new Error(`SpaceAPI responded with ${res.status}`);
        const data: SpaceApiResponse = await res.json();
        if (container) render(container, data);
        if (headerLine) renderHeaderLine(headerLine, data);

        const open = Boolean(data.state.open);
        notifyStatusChange(lastKnownOpen, open);
        lastKnownOpen = open;
      } catch (err) {
        console.error("Konnte SpaceAPI-Status nicht laden", err);
        if (container) renderError(container);
        if (headerLine) renderHeaderLineError(headerLine);
        // Deliberately keep lastKnownOpen: a transient fetch failure shouldn't make the
        // next success look like a status change and fire a false notification.
      } finally {
        inFlight = null;
      }
    })();

    return inFlight;
  };

  void refresh();
  // No point polling (or queuing up notifications) for a tab nobody is looking at.
  setVisibleInterval(() => void refresh(), REFRESH_INTERVAL_MS);

  // Coming back to a backgrounded tab should show the current state right away
  // rather than waiting out the remainder of the interval.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void refresh();
  });
}
